import React, { useEffect, useState, useRef, useCallback } from "react";
import API from "../api";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import { useReactToPrint } from "react-to-print";
import "../css/style.css";
import "../css/pl-report.css";

// ---------------- Helpers ----------------
const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatMoney = (n) => {
    const num = Number(n || 0);
    return "₹ " + num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

// Match POS.jsx / PrintInvoice.jsx label style: "kg" -> "KG", anything else -> "PCS"
const formatType = (t) => {
    const val = String(t || "").toLowerCase();
    if (!val) return "-";
    return val === "kg" ? "KG" : "PCS";
};

const toInputDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Flatten order records into one ledger row per sold item
const flattenSales = (sales) => {
    const rows = [];
    (sales || []).forEach((o) => {
        const items = (o.items && o.items.length) ? o.items : [{ name: "", qty: "-", rate: "-", type: "", total: o.total }];
        items.forEach((it) => {
            rows.push({
                date: o.date,
                customerName: o.customerName,
                invoiceNo: o.invoiceNo,
                item: it.name || "",
                qty: it.qty,
                type: it.sellType || it.type || "",
                rate: it.rate,
                total: it.total
            });
        });
    });
    return rows;
};

// Small inline SVG icons (same look as the design reference)
const BrandIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <path
            d="M4 20V10M10 20V4M16 20V13M22 20H2"
            stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        />
    </svg>
);

const SalesIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" />
        <circle cx="16" cy="16" r="6" stroke="white" strokeWidth="2" />
    </svg>
);

const PurchaseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <path
            d="M3 3h2l2 12h10l2-8H6"
            stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        />
    </svg>
);

const GrossIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <path d="M4 20V10M10 20V4M16 20V13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ExpenseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <rect x="4" y="6" width="16" height="12" rx="2" stroke="white" strokeWidth="2" />
    </svg>
);

const NetIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <path d="M3 17l6-6 4 4 8-8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const NoteIcon = () => (
    <svg viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
        <line x1="12" y1="11" x2="12" y2="16" stroke="white" strokeWidth="2" />
        <circle cx="12" cy="8" r="1" fill="white" />
    </svg>
);
// ---------------- Page ----------------
const ProfitLoss = () => {
    // Default range = current month
    const now = new Date();
    const defaultFrom = toInputDate(new Date(now.getFullYear(), now.getMonth(), 1));
    const defaultTo = toInputDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    const [from, setFrom] = useState(defaultFrom);
    const [to, setTo] = useState(defaultTo);
    const [activeQuick, setActiveQuick] = useState("This Month");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    const printRef = useRef(null);

    // Fetch report from backend
    const fetchReport = useCallback(async () => {
        if (!from || !to) return;

        setLoading(true);
        try {
            const res = await API.get("/reports/profit-loss", {
                params: { from, to }
            });
            setReport(res.data);
        } catch (err) {
            console.log(err);
            toast.error("Failed to load Profit & Loss report");
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Print (react-to-print)
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: "Profit & Loss Report"
    });

    // ---- Date helpers ----
    const applyRange = (fromDate, toDate) => {
        setFrom(toInputDate(fromDate));
        setTo(toInputDate(toDate));
    };

    const applyQuick = (label) => {
        setActiveQuick(label);
        const t = new Date();
        if (label === "Today") {
            applyRange(t, t);
        } else if (label === "This Week") {
            const start = new Date(t);
            const dow = t.getDay() === 0 ? 6 : t.getDay() - 1; // Monday start
            start.setDate(t.getDate() - dow);
            applyRange(start, t);
        } else if (label === "This Month") {
            applyRange(new Date(t.getFullYear(), t.getMonth(), 1), t);
        } else if (label === "3 Months") {
            applyRange(new Date(t.getFullYear(), t.getMonth() - 2, 1), t);
        } else if (label === "6 Months") {
            applyRange(new Date(t.getFullYear(), t.getMonth() - 5, 1), t);
        } else if (label === "1 Year") {
            applyRange(new Date(t.getFullYear() - 1, t.getMonth(), 1), t);
        }
    };

    const resetRange = () => {
        setActiveQuick(null);
        setFrom(defaultFrom);
        setTo(defaultTo);
    };
// Export to Excel (CSV — Excel opens CSV natively)
    const exportCSV = () => {
        if (!report) return;

        const salesRows = flattenSales(report.sales || []);
        const purchases = report.purchases || [];
        const directExpenses = report.directExpenses || [];
        const indirectExpenses = report.indirectExpenses || [];
        const otherIncome = report.otherIncome || [];
        const openingStock = report.openingStockDetails || [];
        const closingStock = report.closingStockDetails || [];
        const t = report.totals || {};

        const lines = [];
        const add = (cells) => lines.push(cells.map(String).map((s) => {
            const cell = String(s ?? "");
            return /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
        }).join(","));

        add(["PROFIT & LOSS REPORT"]);
        add(["Period From", from, "To", to]);
        add([]);

        add(["OPENING STOCK"]);
        add(["Product", "Category", "PCS Qty", "PCS Rate", "KG Qty", "KG Rate", "Value"]);
        openingStock.forEach((r) => add([r.name, r.category, r.qty, r.qtyCost, r.kg, r.kgCost, r.value]));
        add(["TOTAL OPENING STOCK", "", "", "", "", "", t.openingStock || 0]);
        add([]);

        add(["PURCHASES (By / Cr.)"]);
        add(["Date", "Invoice / Ref", "Supplier Name", "Particulars (Item)", "Qty", "Unit", "Purchase Rate", "Total Cost"]);
        purchases.forEach((p) => add([
            formatDate(p.date),
            p.invoiceNo || String(p._id).slice(-6),
            p.supplierName || p.company,
            p.productName,
            p.qty,
            formatType(p.type),
            p.costPrice,
            p.subtotal
        ]));
        add(["TOTAL PURCHASES", "", "", "", "", "", "", t.totalPurchases || 0]);
        add([]);

        add(["DIRECT EXPENSES (INCLUDED IN COGS)"]);
        add(["Date", "Category", "Particulars", "Amount"]);
        directExpenses.forEach((e) => add([formatDate(e.date), e.category, e.particulars, e.amount]));
        add(["TOTAL DIRECT EXPENSES", "", "", t.totalDirectExpenses || 0]);
        add([]);

        add(["COST OF GOODS AVAILABLE"]);
        add(["Opening Stock", t.openingStock || 0]);
        add(["Purchases", t.totalPurchases || 0]);
        add(["Direct Expenses", t.totalDirectExpenses || 0]);
        add(["COST OF GOODS AVAILABLE", t.costOfGoodsAvailable || 0]);
        add([]);

        add(["CLOSING STOCK"]);
        add(["Product", "Category", "PCS Qty", "PCS Rate", "KG Qty", "KG Rate", "Value"]);
        closingStock.forEach((r) => add([r.name, r.category, r.qty, r.qtyCost, r.kg, r.kgCost, r.value]));
        add(["TOTAL CLOSING STOCK", "", "", "", "", "", t.closingStock || 0]);
        add([]);

        add(["COST OF GOODS SOLD (COGS)"]);
        add(["Cost of Goods Available", t.costOfGoodsAvailable || 0]);
        add(["Less: Closing Stock", t.closingStock || 0]);
        add(["COGS", t.cogs || 0]);
        add([]);

        add(["SALES (To / Dr.)"]);
        add(["Date", "Invoice No", "Customer Name", "Particulars (Item)", "Qty", "Unit", "Selling Rate", "Total Sales"]);
        salesRows.forEach((r) => add([
            formatDate(r.date),
            r.invoiceNo,
            r.customerName,
            r.item,
            r.qty,
            formatType(r.type),
            r.rate,
            r.total
        ]));
        add(["TOTAL SALES", "", "", "", "", "", "", t.totalSales || 0]);
        add([]);
add(["OTHER INCOME"]);
        add(["Date", "Category", "Particulars", "Amount"]);
        otherIncome.forEach((i) => add([formatDate(i.date), i.category, i.particulars, i.amount]));
        add(["TOTAL OTHER INCOME", "", "", t.totalOtherIncome || 0]);
        add([]);

        add(["INDIRECT / OPERATING EXPENSES"]);
        add(["Date", "Category", "Particulars", "Amount"]);
        indirectExpenses.forEach((e) => add([formatDate(e.date), e.category, e.particulars, e.amount]));
        add(["TOTAL INDIRECT EXPENSES", "", "", t.totalIndirectExpenses || 0]);
        add([]);

        add(["PURCHASE RETURNS (Refunds)"]);
        add(["Date", "Supplier Name", "Item", "Qty", "Refund Amount"]);
        (report.returns || []).forEach((r) => add([
            formatDate(r.date),
            r.supplierName || r.company,
            r.productName,
            r.qty,
            r.refundAmount
        ]));
        add(["TOTAL RETURNS", "", "", "", t.totalReturns || 0]);
        add([]);

        add(["GROSS PROFIT / LOSS"]);
        add(["Total Income (Sales + Other Income)", t.totalIncome || 0]);
        add(["Less: COGS", t.cogs || 0]);
        add([(t.grossProfit || 0) >= 0 ? "GROSS PROFIT" : "GROSS LOSS", Math.abs(t.grossProfit || 0)]);
        add([]);

        add(["NET PROFIT / LOSS"]);
        add(["Gross Profit / Loss", t.grossProfit || 0]);
        add(["Add: Other Income", t.totalOtherIncome || 0]);
        add(["Less: Indirect Expenses", t.totalIndirectExpenses || 0]);
        add([(t.netProfit || 0) >= 0 ? "NET PROFIT" : "NET LOSS", Math.abs(t.netProfit || 0)]);
        add([]);

        add(["CALCULATION SUMMARY"]);
        add(["STEP 1: Cost of Goods Available = Opening Stock + Purchases + Direct Expenses", t.costOfGoodsAvailable || 0]);
        add(["STEP 2: COGS = Cost of Goods Available - Closing Stock", t.cogs || 0]);
        add(["STEP 3: Gross Profit/Loss = Sales Revenue - COGS", t.grossProfit || 0]);
        add(["STEP 4: Net Profit/Loss = Gross Profit/Loss + Other Income - Indirect Expenses", t.netProfit || 0]);

        const csv = "\uFEFF" + lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `profit-loss-${from}-to-${to}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };
const totals = report?.totals || {
        totalSales: 0,
        totalPurchases: 0,
        totalReturns: 0,
        totalDirectExpenses: 0,
        totalIndirectExpenses: 0,
        totalOtherIncome: 0,
        openingStock: 0,
        closingStock: 0,
        costOfGoodsAvailable: 0,
        cogs: 0,
        grossProfit: 0,
        totalIncome: 0,
        netProfit: 0
    };
    const salesRows = flattenSales(report?.sales || []);
    const purchases = report?.purchases || [];
    const returns = report?.returns || [];
    const directExpenses = report?.directExpenses || [];
    const indirectExpenses = report?.indirectExpenses || [];

    const grossProfit = Number(totals.grossProfit || 0);
    const grossLabel = grossProfit >= 0 ? "Gross Profit" : "Gross Loss";
    const grossAbs = Math.abs(grossProfit);
    const grossCardCls = grossProfit >= 0 ? "plr-sc-purple" : "plr-sc-red";

    const netProfit = Number(totals.netProfit || 0);
    const netLabel = netProfit >= 0 ? "Net Profit" : "Net Loss";
    const netAbs = Math.abs(netProfit);
    const netCardCls = netProfit >= 0 ? "plr-sc-teal" : "plr-sc-red";

    const totalExpenses = Number(totals.totalDirectExpenses || 0) + Number(totals.totalIndirectExpenses || 0);
    const quickButtons = ["Today", "This Week", "This Month", "3 Months", "6 Months", "1 Year"];

    return (
        <Layout>
            <div className="plr">
                <div className="plr-page">

                    {/* ====== HEADER ====== */}
                    <div className="pl-print-area">
                        <div className="plr-header">
                            <div className="plr-brand">
                                <div className="plr-brand-icon"><BrandIcon /></div>
                                <div className="plr-brand-text">
                                    <h1>Profit &amp; Loss A/C</h1>
                                    <p>Stock Management System</p>
                                </div>
                            </div>

                            <div className="plr-date-field">
                                <label>📅 From Date</label>
                                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                            </div>
                            <div className="plr-date-field">
                                <label>📅 To Date</label>
                                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                            </div>
                            <button className="plr-btn plr-btn-primary" onClick={fetchReport} disabled={loading}>
                                {loading ? "Loading..." : "Apply"}
                            </button>
                            <button className="plr-btn" onClick={resetRange}>Reset</button>

                            <div className="plr-header-actions">
                                <div className="plr-quick-ranges">
                                    {quickButtons.map((label) => (
                                        <button
                                            key={label}
                                            className={`plr-btn ${activeQuick === label ? "plr-btn-active" : ""}`}
                                            onClick={() => applyQuick(label)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <button className="plr-btn plr-btn-green" onClick={exportCSV} disabled={!report}>
                                    📊 Export Excel
                                </button>
                                <button className="plr-btn plr-btn-blue-solid" onClick={handlePrint} disabled={!report}>
                                    🖨 Print
                                </button>
                            </div>
                        </div>
{loading && !report && (
                            <div className="ui-loader">
                                <div className="ui-spinner"></div>
                                <span>Loading report…</span>
                            </div>
                        )}

                        {report && (
                            <React.Fragment>
                            {/* ====== SUMMARY CARDS ====== */}
                            <div className="plr-summary-row">
                                <div className="plr-summary-card plr-sc-blue">
                                    <div className="plr-icon"><SalesIcon /></div>
                                    <div>
                                        <p className="plr-label">Total Sales</p>
                                        <p className="plr-value">{formatMoney(totals.totalSales)}</p>
                                    </div>
                                </div>
                                <div className="plr-summary-card plr-sc-green">
                                    <div className="plr-icon"><PurchaseIcon /></div>
                                    <div>
                                        <p className="plr-label">Total Purchases</p>
                                        <p className="plr-value">{formatMoney(totals.totalPurchases)}</p>
                                    </div>
                                </div>
                                <div className={`plr-summary-card ${grossCardCls}`}>
                                    <div className="plr-icon"><GrossIcon /></div>
                                    <div>
                                        <p className="plr-label">{grossLabel}</p>
                                        <p className="plr-value">{formatMoney(grossAbs)}</p>
                                    </div>
                                </div>
                                <div className="plr-summary-card plr-sc-orange">
                                    <div className="plr-icon"><ExpenseIcon /></div>
                                    <div>
                                        <p className="plr-label">Total Expenses</p>
                                        <p className="plr-value">{formatMoney(totalExpenses)}</p>
                                    </div>
                                </div>
                                <div className={`plr-summary-card ${netCardCls}`}>
                                    <div className="plr-icon"><NetIcon /></div>
                                    <div>
                                        <p className="plr-label">{netLabel}</p>
                                        <p className="plr-value">{formatMoney(netAbs)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* ====== MAIN TWO PANELS ====== */}
                            <div className="plr-main-row">
                                {/* Expenses / Cost */}
                                <div className="plr-panel">
                                    <div className="plr-panel-head plr-expenses">
                                        <span>EXPENSES / COST</span>
                                        <span className="plr-amt-label">Amount (₹)</span>
                                    </div>
                                    <table>
                                        <tbody>
                                            <tr className="plr-section-title">
                                                <td>1. Opening Stock</td>
                                                <td className="plr-amt">{formatMoney(totals.openingStock)}</td>
                                            </tr>

                                            <tr className="plr-section-title"><td>2. Purchase / Cost of Goods</td><td></td></tr>
                                            <tr className="plr-item">
                                                <td>Purchases</td>
                                                <td className="plr-amt">{formatMoney(totals.totalPurchases)}</td>
                                            </tr>
                                            <tr className="plr-subtotal"><td></td><td className="plr-amt">{formatMoney(totals.totalPurchases)}</td></tr>

                                            <tr className="plr-section-title"><td>3. Direct Expenses (Purchase Related)</td><td></td></tr>
{directExpenses.map((e, i) => (
                                                <tr className="plr-item" key={`de-${i}`}>
                                                    <td>{e.category}</td>
                                                    <td className="plr-amt">{formatMoney(e.amount)}</td>
                                                </tr>
                                            ))}
                                            {directExpenses.length === 0 && (
                                                <tr className="plr-item"><td>No direct expenses recorded</td><td className="plr-amt">{formatMoney(0)}</td></tr>
                                            )}
                                            <tr className="plr-subtotal"><td></td><td className="plr-amt">{formatMoney(totals.totalDirectExpenses)}</td></tr>

                                            <tr className="plr-strip">
                                                <td>Total Cost of Goods Available</td>
                                                <td className="plr-amt">{formatMoney(totals.costOfGoodsAvailable)}</td>
                                            </tr>
                                            <tr className="plr-plain">
                                                <td><b>Less:</b> Closing Stock</td>
                                                <td className="plr-amt">{formatMoney(totals.closingStock)}</td>
                                            </tr>
                                            <tr className="plr-strip">
                                                <td>Cost of Goods Sold (COGS)</td>
                                                <td className="plr-amt">{formatMoney(totals.cogs)}</td>
                                            </tr>
                                            <tr className={grossProfit >= 0 ? "plr-strip plr-strip-blue" : "plr-strip plr-strip-red"}>
                                                <td>{grossLabel} (from Sales)</td>
                                                <td className="plr-amt">{formatMoney(grossAbs)}</td>
                                            </tr>

                                            <tr className="plr-section-title"><td>4. Operating / Indirect Expenses</td><td></td></tr>
                                            {indirectExpenses.map((e, i) => (
                                                <tr className="plr-item" key={`ie-${i}`}>
                                                    <td>{e.category}</td>
                                                    <td className="plr-amt">{formatMoney(e.amount)}</td>
                                                </tr>
                                            ))}
                                            {indirectExpenses.length === 0 && (
                                                <tr className="plr-item"><td>No indirect expenses recorded</td><td className="plr-amt">{formatMoney(0)}</td></tr>
                                            )}
                                            <tr className="plr-subtotal">
                                                <td style={{ paddingLeft: 32 }}>Total Operating Expenses</td>
                                                <td className="plr-amt">{formatMoney(totals.totalIndirectExpenses)}</td>
                                            </tr>

                                            <tr className={netProfit >= 0 ? "plr-net-profit" : "plr-net-loss"}>
                                                <td>{netLabel}</td>
                                                <td className="plr-amt">{formatMoney(netAbs)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                {/* Income / Revenue */}
                                <div className="plr-panel">
                                    <div className="plr-panel-head plr-income">
                                        <span>INCOME / REVENUE</span>
                                        <span className="plr-amt-label">Amount (₹)</span>
                                    </div>
<table>
                                        <tbody>
                                            <tr className="plr-section-title"><td>1. Sales / Revenue</td><td></td></tr>
                                            <tr className="plr-item">
                                                <td>Sales Revenue</td>
                                                <td className="plr-amt">{formatMoney(totals.totalSales)}</td>
                                            </tr>
                                            <tr className="plr-item">
                                                <td>Other Income</td>
                                                <td className="plr-amt">{formatMoney(totals.totalOtherIncome)}</td>
                                            </tr>
                                            <tr className="plr-strip plr-strip-green">
                                                <td>Total Income</td>
                                                <td className="plr-amt">{formatMoney(totals.totalIncome)}</td>
                                            </tr>

                                            <tr className="plr-section-title"><td>2. Less: Cost of Goods Sold (COGS)</td><td></td></tr>
                                            <tr className="plr-item">
                                                <td>(From Left Side)</td>
                                                <td className="plr-amt">{formatMoney(totals.cogs)}</td>
                                            </tr>

                                            <tr className="plr-section-title"><td>3. {grossLabel}</td><td></td></tr>
                                            <tr className={grossProfit >= 0 ? "plr-strip plr-strip-blue" : "plr-strip plr-strip-red"}>
                                                <td style={{ fontWeight: 700 }}></td>
                                                <td className="plr-amt">{formatMoney(grossAbs)}</td>
                                            </tr>

                                            <tr className="plr-section-title"><td>4. Less: Operating / Indirect Expenses</td><td></td></tr>
                                            <tr className="plr-item">
                                                <td>(From Left Side)</td>
                                                <td className="plr-amt">{formatMoney(totals.totalIndirectExpenses)}</td>
                                            </tr>

                                            <tr className={netProfit >= 0 ? "plr-net-profit" : "plr-net-loss"}>
                                                <td>{netLabel}</td>
                                                <td className="plr-amt">{formatMoney(netAbs)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
{/* ====== DETAILS TABLES (Purchase / Sales) ====== */}
                            <div className="plr-details-row">
                                <div className="plr-panel">
                                    <div className="plr-details-head plr-purchase">Purchase Details</div>
                                    <div className="plr-details-scroll">
                                        <table className="plr-details-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Inv. No</th>
                                                    <th>Supplier</th>
                                                    <th>Particulars</th>
                                                    <th className="num">Qty</th>
                                                    <th>Unit</th>
                                                    <th className="num">Rate</th>
                                                    <th className="num">Total (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {purchases.map((p, i) => (
                                                    <tr key={`p-${i}`}>
                                                        <td>{formatDate(p.date)}</td>
                                                        <td>{p.invoiceNo || String(p._id).slice(-6)}</td>
                                                        <td>{p.supplierName || p.company}</td>
                                                        <td>{p.productName}</td>
                                                        <td className="num">{p.qty}</td>
                                                        <td>{formatType(p.type)}</td>
                                                        <td className="num">{p.costPrice}</td>
                                                        <td className="num">{formatMoney(p.subtotal)}</td>
                                                    </tr>
                                                ))}
                                                {purchases.length === 0 && (
                                                    <tr className="plr-empty"><td colSpan="8">No purchases found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="plr-details-total">
                                        <span>Total Purchase Cost</span>
                                        <span>{formatMoney(totals.totalPurchases)}</span>
                                    </div>
                                </div>

                                <div className="plr-panel">
                                    <div className="plr-details-head plr-sales">Sales Details</div>
                                    <div className="plr-details-scroll">
                                        <table className="plr-details-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Inv. No</th>
                                                    <th>Customer</th>
                                                    <th>Particulars</th>
                                                    <th className="num">Qty</th>
                                                    <th>Unit</th>
                                                    <th className="num">Rate</th>
                                                    <th className="num">Total (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {salesRows.map((r, i) => (
                                                    <tr key={`s-${i}`}>
                                                        <td>{formatDate(r.date)}</td>
                                                        <td>{r.invoiceNo}</td>
                                                        <td>{r.customerName}</td>
                                                        <td>{r.item}</td>
                                                        <td className="num">{r.qty}</td>
                                                        <td>{formatType(r.type)}</td>
                                                        <td className="num">{r.rate}</td>
                                                        <td className="num">{formatMoney(r.total)}</td>
                                                    </tr>
                                                ))}
                                                {salesRows.length === 0 && (
                                                    <tr className="plr-empty"><td colSpan="8">No sales found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="plr-details-total">
                                        <span>Total Sales Revenue</span>
                                        <span>{formatMoney(totals.totalSales)}</span>
                                    </div>
                                </div>
                            </div>
{/* ====== PURCHASE RETURNS ====== */}
                            {returns.length > 0 && (
                                <div className="plr-panel" style={{ marginBottom: 16 }}>
                                    <div className="plr-details-head plr-plain">Purchase Returns (Refund Amount)</div>
                                    <div className="plr-details-scroll">
                                        <table className="plr-details-table">
                                            <thead>
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Supplier Name</th>
                                                    <th>Company</th>
                                                    <th>Item</th>
                                                    <th className="num">Qty</th>
                                                    <th className="num">Refund Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {returns.map((r, i) => (
                                                    <tr key={`r-${i}`}>
                                                        <td>{formatDate(r.date)}</td>
                                                        <td>{r.supplierName}</td>
                                                        <td>{r.company}</td>
                                                        <td>{r.productName}</td>
                                                        <td className="num">{r.qty}</td>
                                                        <td className="num">{formatMoney(r.refundAmount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="plr-details-total">
                                        <span>Total Refund</span>
                                        <span>{formatMoney(totals.totalReturns)}</span>
                                    </div>
                                </div>
                            )}

{/* ====== CALCULATION SUMMARY ====== */}
                            <div className="plr-panel" style={{ marginBottom: 16 }}>
                                <div className="plr-details-head plr-plain">Calculation Summary</div>
                                <div className="plr-details-scroll">
                                    <table className="plr-details-table">
                                        <thead>
                                            <tr>
                                                <th>Step</th>
                                                <th>Formula</th>
                                                <th className="num">Amount (₹)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>STEP 1</td>
                                                <td>Cost of Goods Available = Opening Stock + Purchases + Direct Expenses</td>
                                                <td className="num">{formatMoney(totals.costOfGoodsAvailable)}</td>
                                            </tr>
                                            <tr>
                                                <td>STEP 2</td>
                                                <td>COGS = Cost of Goods Available − Closing Stock</td>
                                                <td className="num">{formatMoney(totals.cogs)}</td>
                                            </tr>
                                            <tr>
                                                <td>STEP 3</td>
                                                <td>Gross Profit / Loss = Sales Revenue − COGS</td>
                                                <td className="num">{formatMoney(grossAbs)}</td>
                                            </tr>
                                            <tr>
                                                <td>STEP 4</td>
                                                <td>Net Profit / Loss = Gross Profit / Loss + Other Income − Indirect Expenses</td>
                                                <td className="num">{formatMoney(netAbs)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* ====== BOTTOM ROW ====== */}
                            <div className="plr-bottom-row">
                                <div className="plr-quick-summary">
                                    <div className="plr-qs-title"><span className="plr-qs-icon">📄</span> Quick Summary</div>
                                    <div className="plr-qs-item">Total Purchase : <span className="value">{formatMoney(totals.totalPurchases)}</span></div>
                                    <div className="plr-divider-v"></div>
                                    <div className="plr-qs-item">Total Sales : <span className="value">{formatMoney(totals.totalSales)}</span></div>
                                    <div className="plr-divider-v"></div>
                                    <div className="plr-qs-item">{grossLabel} : <span className="value">{formatMoney(grossAbs)}</span></div>
                                    <div className="plr-divider-v"></div>
                                    <div className="plr-qs-item">Total Expense : <span className="value">{formatMoney(totalExpenses)}</span></div>
                                    <div className="plr-divider-v"></div>
                                    <div className="plr-qs-item">{netLabel} : <span className="value">{formatMoney(netAbs)}</span></div>
                                </div>

                                <div className="plr-note-box">
                                    <div className="plr-note-icon"><NoteIcon /></div>
                                    <div className="plr-note-text">
                                        <strong>Note:</strong>
                                        <p>
                                            All amounts are calculated based on actual transaction data from the selected
                                            period ({formatDate(from)} — {formatDate(to)}).
                                        </p>
                                    </div>
                                </div>
                            </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ProfitLoss;